import { z } from 'zod'
import { Commitment } from './m2.ts'

/**
 * M4 strand B: light CRM (§19.6), commitment tracker (§19.1), meeting prep (§16.3), weekly review (§16.4).
 * Paths below /api/v1.
 */

const id = z.uuid()
const isoDateTime = z.iso.datetime({ offset: true })
const isoDate = z.iso.date()

// ── People ─────────────────────────────────────────────────────────────────────────────────────
// GET /people?q=&containerId=&sort=name|contact|due → Person[] (m2 Person incl. the CRM fields below)
// PATCH /people/:id accepts UpsertPerson (m2) + aliases, contactIntervalDays, followUpOn

/** POST /people/:id/merge – the other people are folded into :id (commitments, links, projects, e-mails, aliases)
 * and moved to the trash. Returns the PersonCard of :id. */
export const MergePeople = z.object({ mergeIds: z.array(id).min(1).max(20) })
export type MergePeople = z.infer<typeof MergePeople>

/** GET /people/duplicates → groups of people that look like the same person (same e-mail or very similar name) */
export const DuplicateGroup = z.object({
  reason: z.enum(['email', 'name']),
  people: z.array(
    z.object({ id, name: z.string(), emails: z.array(z.string()), company: z.string().nullable() }),
  ),
})
export type DuplicateGroup = z.infer<typeof DuplicateGroup>

/**
 * GET /people/attendees → meeting attendees not linked to a person yet ("Personen aus Teilnehmern anlegen").
 * key = lower-cased e-mail, else 'name:<normalized name>'.
 */
export const AttendeeCandidate = z.object({
  key: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  meetings: z.number().int(),
  lastMeetingAt: isoDateTime,
  sampleMeetings: z.array(z.object({ id, title: z.string(), startedAt: isoDateTime })),
  /** existing people with a similar name or the same e-mail domain (best first) */
  suggestions: z.array(z.object({ personId: id, name: z.string(), score: z.number() })),
})
export type AttendeeCandidate = z.infer<typeof AttendeeCandidate>

/** POST /people/attendees/resolve – the user's review: create, link to an existing person (adds the e-mail or the
 * name as alias) or ignore (not suggested again). Nothing is created without such a decision. */
export const ResolveAttendees = z.object({
  items: z
    .array(
      z.discriminatedUnion('action', [
        z.object({
          key: z.string().max(400),
          action: z.literal('create'),
          name: z.string().trim().min(1).max(200),
          email: z.email().nullable().optional(),
          company: z.string().max(200).nullable().optional(),
        }),
        z.object({ key: z.string().max(400), action: z.literal('link'), personId: id }),
        z.object({ key: z.string().max(400), action: z.literal('ignore') }),
      ]),
    )
    .min(1)
    .max(500),
})
export type ResolveAttendees = z.infer<typeof ResolveAttendees>
export const ResolveAttendeesResult = z.object({
  created: z.number().int(),
  linked: z.number().int(),
  ignored: z.number().int(),
})

// ── Commitments (§19.1) ────────────────────────────────────────────────────────────────────────
// GET /commitments?direction=&status=&personId=&containerId=&due=overdue|week
// GET /commitments/summary → CommitmentSummary
export const CommitmentSummary = z.object({
  iOwe: z.object({ open: z.number().int(), overdue: z.number().int(), dueThisWeek: z.number().int() }),
  owedToMe: z.object({ open: z.number().int(), overdue: z.number().int(), dueThisWeek: z.number().int() }),
})
export type CommitmentSummary = z.infer<typeof CommitmentSummary>

// ── Meeting prep (§16.3) ───────────────────────────────────────────────────────────────────────
/** An upcoming event as the calendar (M4 strand A) or a planned meeting delivers it. */
export const PrepEvent = z.object({
  /** calendar event id, or 'meeting:<id>' for a meeting record */
  key: z.string().min(1).max(400),
  title: z.string(),
  startsAt: isoDateTime,
  endsAt: isoDateTime.nullable().optional(),
  participants: z.array(z.object({ name: z.string(), email: z.string().nullable().optional() })),
  containerId: id.nullable().optional(),
  meetingId: id.nullable().optional(),
})
export type PrepEvent = z.infer<typeof PrepEvent>

export const PrepPerson = z.object({
  /** null = attendee without a person record yet */
  personId: id.nullable(),
  name: z.string(),
  email: z.string().nullable(),
  company: z.string().nullable(),
  role: z.string().nullable(),
  lastContactAt: isoDateTime.nullable(),
})
export const PrepBrief = z.object({
  id: id.nullable(),
  event: PrepEvent,
  container: z.object({ id, title: z.string() }).nullable(),
  people: z.array(PrepPerson),
  lastMeetings: z.array(
    z.object({ id, title: z.string(), startedAt: isoDateTime, summary: z.string(), noteId: id.nullable() }),
  ),
  commitments: z.object({ iOwe: z.array(Commitment), owedToMe: z.array(Commitment) }),
  openTasks: z.array(
    z.object({ id, title: z.string(), dueDate: isoDate.nullable(), containerTitle: z.string().nullable() }),
  ),
  decisions: z.array(z.object({ id, title: z.string(), decidedAt: isoDateTime.nullable() })),
  notes: z.array(z.object({ id, title: z.string(), snippet: z.string() })),
  /** suggested agenda, German, derived from the open items */
  agenda: z.array(z.string()),
  generatedAt: isoDateTime,
})
export type PrepBrief = z.infer<typeof PrepBrief>
// GET  /meetings/:id/prep          → PrepBrief (built on demand, not stored)
// POST /preps { event: PrepEvent } → PrepBrief (stored; same event key updates)
// GET  /preps?upcoming=1           → PrepBrief[] (stored, newest event first)
// GET  /preps/:id                  → PrepBrief
// GET  /people/:id/preps           → PrepBrief[] (stored preps with this person)

// ── Weekly review (§16.4) ──────────────────────────────────────────────────────────────────────
export const ReviewSettings = z.object({
  /** null = off; 1 = Monday … 5 = Friday (default), 0/7 = Sunday */
  weeklyReviewDow: z.number().int().min(0).max(7).nullable(),
  weeklyReviewTime: z.string().regex(/^\d{2}:\d{2}$/),
  /** "lange kein Kontakt" default for people without their own interval (days, null = off) */
  defaultContactIntervalDays: z.number().int().min(1).max(3650).nullable(),
})
export type ReviewSettings = z.infer<typeof ReviewSettings>
// GET/PATCH /crm/settings → ReviewSettings
// POST /reviews/weekly → { noteId } (creates the weekly review note now)
// GET  /reviews/weekly → { notes: { id, title, createdAt }[] }
